/**
 * Progressive-web-app plumbing: installability, connectivity, and the offline
 * queue. Everything here is progressive enhancement — if any of it is missing the
 * app still works, it just stops behaving like an installed app.
 */

export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export const isStandalone = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    // iOS reports it here rather than through the media query
    (navigator as unknown as { standalone?: boolean }).standalone === true)

export const isIOS = (): boolean =>
  typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)

export const isOnline = (): boolean => (typeof navigator === 'undefined' ? true : navigator.onLine)

/** Captures the browser's install prompt so the UI can offer its own button. */
let deferredPrompt: InstallPromptEvent | null = null
const listeners = new Set<(available: boolean) => void>()

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt = event as InstallPromptEvent
    listeners.forEach((fn) => fn(true))
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    listeners.forEach((fn) => fn(false))
  })
}

export function onInstallAvailability(fn: (available: boolean) => void): () => void {
  listeners.add(fn)
  fn(deferredPrompt !== null)
  return () => listeners.delete(fn)
}

/** Resolves to the user's choice, or null when the browser never offered a prompt
 *  (iOS Safari does not implement beforeinstallprompt). */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  if (!deferredPrompt) return null
  await deferredPrompt.prompt()
  const { outcome } = await deferredPrompt.userChoice
  deferredPrompt = null
  listeners.forEach((fn) => fn(false))
  return outcome
}

// ─── Offline queue ───────────────────────────────────────────
// A check-in started while the wardrobe has no signal must not be lost: it is
// stored here and replayed when connectivity returns.

export interface QueuedCheckIn {
  id: string
  venueId: string
  items: Array<{ type: string }>
  createdAt: string
  guestId?: string
}

const QUEUE_KEY = 'zipcheck_checkin_queue'

export function readQueue(): QueuedCheckIn[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeQueue(queue: QueuedCheckIn[]): void {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(queue)) } catch { /* storage full/blocked */ }
}

export function enqueueCheckIn(entry: QueuedCheckIn): number {
  const queue = readQueue()
  queue.push(entry)
  writeQueue(queue)
  return queue.length
}

export function removeFromQueue(id: string): number {
  const queue = readQueue().filter((entry) => entry.id !== id)
  writeQueue(queue)
  return queue.length
}

/**
 * Ask the browser to keep our storage. Without it the queued check-ins live in
 * "best effort" storage and can be evicted when the device runs out of space —
 * exactly when a cloakroom guest is offline and cannot re-create the request.
 */
export async function ensurePersistentStorage(): Promise<boolean> {
  try {
    const s = navigator.storage as StorageManager | undefined
    if (!s?.persist) return false
    if (s.persisted && (await s.persisted())) return true
    return await s.persist()
  } catch {
    return false
  }
}

/**
 * Register a Background Sync tag (Chromium only). NOTE: the generated service
 * worker has no 'sync' listener, so this is the hook for it — a genuine
 * closed-app replay needs the queue moved from localStorage to IndexedDB (a
 * service worker cannot read localStorage), which is the next slice.
 */
export async function registerBackgroundSync(tag = 'zipcheck-outbox'): Promise<boolean> {
  try {
    const reg = (await navigator.serviceWorker?.ready) as unknown as
      | { sync?: { register: (t: string) => Promise<void> } }
      | undefined
    if (!reg?.sync) return false
    await reg.sync.register(tag)
    return true
  } catch {
    return false
  }
}

/**
 * A stale service worker can hold a precache referencing asset files that a new
 * deployment has replaced, which renders a blank page with no way out. Vite fires
 * `vite:preloadError` when a dynamic import 404s; drop the caches, unregister the
 * worker and reload once so the client heals itself instead of staying white.
 */
export function installStaleChunkRecovery(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('vite:preloadError', async () => {
    if (sessionStorage.getItem('pwa-recovered') === '1') return
    sessionStorage.setItem('pwa-recovered', '1')
    try {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
      const regs = await navigator.serviceWorker?.getRegistrations?.() ?? []
      await Promise.all(regs.map((r) => r.unregister()))
    } catch {
      /* nothing more we can do — the reload below is the fallback */
    }
    window.location.reload()
  })
  // When a new worker takes control, reload once so every tab runs the new bundle.
  navigator.serviceWorker?.addEventListener?.('controllerchange', () => {
    if (sessionStorage.getItem('pwa-reloaded') === '1') return
    sessionStorage.setItem('pwa-reloaded', '1')
    window.location.reload()
  })
}

export function newQueueId(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}
